import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { StatusBadge, SelectDrop } from '../components/ui';

/* ─── Data definitions ─────────────────────────────────────────────────── */

// Programs that require FA + IMM in addition to PC + RES
const FULL_DOC_PROGRAMS = ['BMT', 'AMT', 'ATM', 'BRM', 'MOA'];

const DOC_COLS = [
  { key: 'doc_first_aid',         short: 'FA',  full: 'First Aid Cert',    group: 'prereq',    fullDocOnly: true  },
  { key: 'doc_pic',               short: 'PIC', full: 'PIC',               group: 'prereq',    fullDocOnly: true  },
  { key: 'doc_immunization',      short: 'IMM', full: 'Immunization',      group: 'prereq',    fullDocOnly: true  },
  { key: 'doc_resume',            short: 'RES', full: 'Resume',            group: 'prereq',    fullDocOnly: false },
  { key: 'timesheet_uploaded',    short: 'TS',  full: 'Timesheet',              group: 'milestone', fullDocOnly: false },
  { key: 'final_eval_uploaded',   short: 'PE',  full: 'Practicum Eval',         group: 'milestone', fullDocOnly: false },
  { key: 'exit_survey_submitted', short: 'ES',  full: 'Exit Survey',            group: 'milestone', fullDocOnly: false },
];
const PREREQ_COLS    = DOC_COLS.filter(c => c.group === 'prereq');
const MILESTONE_COLS = DOC_COLS.filter(c => c.group === 'milestone');

const STATUS_SYM   = { done: '✓', missing: '✕', na: '—' };
const STATUS_LABEL = { done: 'Complete', missing: 'Missing', na: 'Not required' };
const STATUS_COLOR = { done: '#1F8A5B', missing: '#B3261E', na: '#5C7388' };
const STATUS_BG    = { done: '#E6F3EC', missing: '#FBE7E5', na: '#EEF1F5' };

function isRequired(col, programCode) {
  if (col.fullDocOnly && !FULL_DOC_PROGRAMS.includes(programCode)) return false;
  return true;
}

function resolveStatus(student, col, localDocs) {
  const override = localDocs[student.id];
  const val = override && col.key in override ? override[col.key] : student[col.key];
  if (!isRequired(col, student.program_code)) return 'na';
  return val ? 'done' : 'missing';
}

function studentInitials(s) {
  return `${s.first_name?.[0] || ''}${s.last_name?.[0] || ''}`.toUpperCase();
}

function fmtDate(d) {
  if (!d) return '—';
  if (d === 'no-date') return 'No date assigned';
  try {
    return new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch { return d; }
}

/* ─── Hero ──────────────────────────────────────────────────────────────── */

function DTHero({ cohortDates, selectedDate, onDateChange, missingOnly, onToggleMissing, onExport }) {
  return (
    <div className="h-hero">
      <div className="h-hero-decor" aria-hidden="true">
        <svg
          viewBox="0 0 800 320"
          preserveAspectRatio="xMaxYMid slice"
          style={{ position: 'absolute', right: 0, top: 0, height: '100%', width: 'auto' }}
        >
          <g fill="none" stroke="rgba(245,166,35,0.10)" strokeWidth="1">
            <circle cx="720" cy="160" r="80"/>
            <circle cx="720" cy="160" r="140"/>
            <circle cx="720" cy="160" r="200"/>
            <circle cx="720" cy="160" r="260"/>
          </g>
          <g fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="1">
            <circle cx="540" cy="40" r="180"/>
            <circle cx="540" cy="40" r="240"/>
          </g>
        </svg>
      </div>

      <div className="h-hero-inner">
        <div className="h-hero-top">
          <div>
            <div className="h-hero-crumb">
              <span>Workspace</span>
              <span className="h-crumb-sep">/</span>
              <span>Doc Tracker</span>
            </div>
            <h1 className="h-hero-title">
              Document <span className="h-hero-accent">Tracker</span>
            </h1>
            <p className="h-hero-lede">
              Track pre-requisite documents and placement milestones for each student across the cohort.
            </p>
          </div>

          <div className="h-hero-actions" style={{ gap: 16 }}>
            <div className="dt-cohort">
              <label>Cohort Start</label>
              <select
                value={selectedDate}
                onChange={e => onDateChange(e.target.value)}
                style={{ background: 'transparent', color: 'white', border: 'none', padding: '5px 8px', minWidth: 130, fontSize: 13, cursor: 'pointer', outline: 'none' }}
              >
                {cohortDates.length === 0 && <option value="">Loading…</option>}
                {cohortDates.map(d => (
                  <option key={d} value={d} style={{ background: '#0B1F2E', color: 'white' }}>
                    {fmtDate(d)}
                  </option>
                ))}
              </select>
            </div>

            <label className="dt-toggle">
              <input
                type="checkbox"
                checked={missingOnly}
                onChange={e => onToggleMissing(e.target.checked)}
              />
              <span className="dt-toggle-track">
                <span className="dt-toggle-knob" style={{ transform: missingOnly ? 'translateX(16px)' : 'none' }} />
              </span>
              <span className="dt-toggle-label">Missing only</span>
            </label>

            {selectedDate && (
              <button className="h-btn-ghost" onClick={onExport}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                  <path d="M8 2v8M5 7l3 3 3-3M3 13h10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Export CSV
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── KPI strip ─────────────────────────────────────────────────────────── */

function DTKpis({ students, localDocs }) {
  const total     = students.length;
  const complete  = students.filter(s => DOC_COLS.every(col => resolveStatus(s, col, localDocs) !== 'missing')).length;
  const pending   = total - complete;
  const totalReq  = students.reduce((a, s) => a + DOC_COLS.filter(col => isRequired(col, s.program_code)).length, 0);
  const totalDone = students.reduce((a, s) => a + DOC_COLS.filter(col => resolveStatus(s, col, localDocs) === 'done').length, 0);
  const pct       = totalReq > 0 ? Math.round((totalDone / totalReq) * 100) : 0;

  const kpis = [
    { eyebrow: 'Cohort',   value: total,    label: 'Total students',            bar: '#1F87E5' },
    { eyebrow: 'Complete', value: complete, label: 'All docs submitted',         bar: '#1F8A5B', vColor: complete > 0 ? '#1F8A5B' : undefined },
    { eyebrow: 'Pending',  value: pending,  label: 'Missing ≥ 1 document',       bar: '#D14343', vColor: pending > 0 ? '#B3261E' : undefined },
    { eyebrow: 'Overall',  value: `${pct}%`,label: `${totalDone} of ${totalReq} required docs`, bar: '#F5A623' },
  ];

  return (
    <div className="dt-kpis">
      {kpis.map(k => (
        <div className="dt-kpi" key={k.eyebrow}>
          <div className="dt-kpi-bar" style={{ background: k.bar }} />
          <div className="dt-kpi-eyebrow">{k.eyebrow}</div>
          <div className="dt-kpi-value" style={k.vColor ? { color: k.vColor } : undefined}>{k.value}</div>
          <div className="dt-kpi-label">{k.label}</div>
        </div>
      ))}
    </div>
  );
}

/* ─── Filter bar ────────────────────────────────────────────────────────── */

function DTFilters({ students, localDocs, search, onSearch, chip, onChip, program, onProgram, isAdmin }) {
  const programs = [...new Set(students.map(s => s.program_name).filter(Boolean))].sort();

  const counts = {
    all:       students.length,
    prereq:    students.filter(s => PREREQ_COLS.some(col    => resolveStatus(s, col, localDocs) === 'missing')).length,
    milestone: students.filter(s => MILESTONE_COLS.some(col => resolveStatus(s, col, localDocs) === 'missing')).length,
    complete:  students.filter(s => DOC_COLS.every(col      => resolveStatus(s, col, localDocs) !== 'missing')).length,
  };

  const CHIPS = [
    { key: 'all',       label: 'All students',        dot: null },
    { key: 'prereq',    label: 'Pre-req incomplete',  dot: '#B3261E' },
    { key: 'milestone', label: 'Milestones pending',  dot: '#B47200' },
    { key: 'complete',  label: 'All complete',        dot: '#1F8A5B' },
  ];

  return (
    <div className="dt-filter-bar">
      <div className="dt-search">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <circle cx="6.5" cy="6.5" r="5" stroke="currentColor" strokeWidth="1.6"/>
          <path d="m10.5 10.5 3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
        </svg>
        <input
          placeholder="Search students…"
          value={search}
          onChange={e => onSearch(e.target.value)}
        />
      </div>
      <div className="dt-chips">
        {CHIPS.map(c => (
          <button
            key={c.key}
            className={`dt-chip${chip === c.key ? ' active' : ''}`}
            onClick={() => onChip(c.key)}
          >
            {c.dot && <span className="dt-chip-dot" style={{ background: c.dot }} />}
            {c.label}
            <span className="dt-chip-n">{counts[c.key]}</span>
          </button>
        ))}
      </div>
      <div className="dt-spacer" />
      {isAdmin && programs.length > 0 && (
        <SelectDrop
          minWidth={160}
          value={program}
          onChange={onProgram}
          options={[{ value: '', label: 'All programs' }, ...programs.map(p => ({ value: p, label: p }))]}
          style={{ flexShrink: 0 }}
        />
      )}
    </div>
  );
}

/* ─── Matrix sub-components ─────────────────────────────────────────────── */

function ColHead({ col, students, localDocs }) {
  const applicable = students.filter(s => isRequired(col, s.program_code));
  if (applicable.length === 0) {
    return (
      <div className="dt-colhead" title={col.full}>
        <div className="dt-colhead-label">{col.short}</div>
        <div className="dt-colhead-pct" style={{ color: 'var(--ink-4)' }}>—</div>
      </div>
    );
  }
  const done = applicable.filter(s => resolveStatus(s, col, localDocs) === 'done').length;
  const pct  = Math.round((done / applicable.length) * 100);
  const fill = pct >= 75 ? '#1F8A5B' : pct >= 30 ? '#B47200' : '#B3261E';
  return (
    <div className="dt-colhead" title={`${col.full} — ${pct}% (${done}/${applicable.length})`}>
      <div className="dt-colhead-label">{col.short}</div>
      <div className="dt-colhead-pct" style={{ color: fill }}>{pct}%</div>
      <div className="dt-colhead-bar">
        <div className="dt-colhead-fill" style={{ width: `${pct}%`, background: '#F5A623' }} />
      </div>
    </div>
  );
}

function DocCell({ student, col, localDocs, onToggle }) {
  const status = resolveStatus(student, col, localDocs);
  return (
    <button
      className={`doc-cell s-${status}`}
      style={{ color: STATUS_COLOR[status], background: STATUS_BG[status], cursor: status === 'na' ? 'default' : 'pointer', border: 'none' }}
      title={`${col.full}: ${STATUS_LABEL[status]}${status !== 'na' ? ' — click to toggle' : ''}`}
      onClick={e => { e.stopPropagation(); if (status !== 'na') onToggle(student.id, col.key); }}
    >
      {STATUS_SYM[status]}
    </button>
  );
}

/* ─── Student drawer ─────────────────────────────────────────────────────── */

function StudentDrawer({ studentId, students, localDocs, onToggle, onClose }) {
  const student = students.find(s => s.id === studentId);
  if (!student) return null;

  const ini = studentInitials(student);
  const missingCount = DOC_COLS.filter(col => resolveStatus(student, col, localDocs) === 'missing').length;

  return (
    <>
      <div className="dp-scrim" onClick={onClose} />
      <aside className="dp dp-wide" style={{ display: 'flex', flexDirection: 'column' }}>
        <div className="dp-head">
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: 'linear-gradient(135deg,#F5A623,#C97A0E)',
              color: '#0B1F2E', fontWeight: 700, fontSize: 15,
              display: 'grid', placeItems: 'center', flexShrink: 0
            }}>
              {ini}
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, color: '#fff', lineHeight: 1.2 }}>
                {student.first_name} {student.last_name}
              </div>
              <div className="dp-substack" style={{ marginTop: 4 }}>
                <span>{student.program_name || student.program_code}</span>
                {student.pc_name && <span style={{ opacity: 0.6 }}>· {student.pc_name}</span>}
              </div>
            </div>
          </div>
          <button className="dp-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="dp-body" style={{ flex: 1, overflowY: 'auto' }}>
          <div style={{ padding: '18px 20px' }}>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <StatusBadge status={student.status} />
              {missingCount > 0
                ? <span className="missing-badge">{missingCount} missing</span>
                : <span className="missing-badge zero">All complete</span>
              }
            </div>

            <div className="dp-section-label">Pre-requisite documents</div>
            <div className="dp-docs" style={{ marginBottom: 18 }}>
              {PREREQ_COLS.map(col => {
                const status = resolveStatus(student, col, localDocs);
                return (
                  <button
                    key={col.key}
                    className="dp-doc-row"
                    onClick={() => status !== 'na' && onToggle(student.id, col.key)}
                    style={{ width: '100%', cursor: status === 'na' ? 'default' : 'pointer' }}
                  >
                    <div className={`dp-doc-cell s-${status}`} style={{ color: STATUS_COLOR[status], background: STATUS_BG[status] }}>
                      {STATUS_SYM[status]}
                    </div>
                    <div className="dp-doc-meta">
                      <div className="dp-doc-name">{col.full}</div>
                      <div className="dp-doc-status" style={{ color: STATUS_COLOR[status] }}>{STATUS_LABEL[status]}</div>
                    </div>
                    {status !== 'na' && (
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ opacity: 0.35, flexShrink: 0 }}>
                        <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="dp-section-label">Placement milestones</div>
            <div className="dp-docs" style={{ marginBottom: 24 }}>
              {MILESTONE_COLS.map(col => {
                const status = resolveStatus(student, col, localDocs);
                return (
                  <button
                    key={col.key}
                    className="dp-doc-row"
                    onClick={() => status !== 'na' && onToggle(student.id, col.key)}
                    style={{ width: '100%', cursor: status === 'na' ? 'default' : 'pointer' }}
                  >
                    <div className={`dp-doc-cell s-${status}`} style={{ color: STATUS_COLOR[status], background: STATUS_BG[status] }}>
                      {STATUS_SYM[status]}
                    </div>
                    <div className="dp-doc-meta">
                      <div className="dp-doc-name">{col.full}</div>
                      <div className="dp-doc-status" style={{ color: STATUS_COLOR[status] }}>{STATUS_LABEL[status]}</div>
                    </div>
                    {status !== 'na' && (
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none" style={{ opacity: 0.35, flexShrink: 0 }}>
                        <path d="M6 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>

            <Link
              to={`/students/${student.id}`}
              className="btn btn-primary"
              style={{ display: 'block', textAlign: 'center', textDecoration: 'none', fontSize: 13 }}
              onClick={onClose}
            >
              View Full Profile
            </Link>
          </div>
        </div>
      </aside>
    </>
  );
}

/* ─── Matrix ────────────────────────────────────────────────────────────── */

function DocMatrix({ students, localDocs, onToggle, onRowClick }) {
  if (students.length === 0) return null;

  return (
    <div className="dt-matrix-wrap">
      <div className="dt-matrix">

        {/* Header row */}
        <div className="dt-row dt-row-head">
          <div className="dt-cell-student">Student</div>
          <div>Program</div>
          <div>Status</div>
          <div className="dt-group-prereq">
            <div className="dt-group-label">Pre-Req Documents</div>
            {PREREQ_COLS.map(col => (
              <ColHead key={col.key} col={col} students={students} localDocs={localDocs} />
            ))}
          </div>
          <div className="dt-group-milestone">
            <div className="dt-group-label">Milestones</div>
            {MILESTONE_COLS.map(col => (
              <ColHead key={col.key} col={col} students={students} localDocs={localDocs} />
            ))}
          </div>
          <div className="dt-cell-missing">✕</div>
        </div>

        {/* Body rows */}
        {students.map(student => {
          const missingCount = DOC_COLS.filter(col => resolveStatus(student, col, localDocs) === 'missing').length;
          const ini = studentInitials(student);
          return (
            <div
              key={student.id}
              className="dt-row dt-row-body"
              onClick={() => onRowClick(student.id)}
            >
              <div className="dt-cell-student">
                <div className="dt-avatar">{ini}</div>
                <div className="dt-stu-meta">
                  <div className="dt-stu-name">{student.last_name}, {student.first_name}</div>
                  {student.pc_name && <div className="dt-stu-pc">{student.pc_name}</div>}
                </div>
              </div>
              <div className="dt-cell-program">{student.program_name || student.program_code || '—'}</div>
              <div><StatusBadge status={student.status} /></div>
              <div className="dt-group-prereq">
                <div className="dt-group-label" />
                {PREREQ_COLS.map(col => (
                  <DocCell key={col.key} student={student} col={col} localDocs={localDocs} onToggle={onToggle} />
                ))}
              </div>
              <div className="dt-group-milestone">
                <div className="dt-group-label" />
                {MILESTONE_COLS.map(col => (
                  <DocCell key={col.key} student={student} col={col} localDocs={localDocs} onToggle={onToggle} />
                ))}
              </div>
              <div className="dt-cell-missing">
                <span className={`missing-badge${missingCount === 0 ? ' zero' : ''}`}>{missingCount}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="dt-legend">
        <span>
          <span className="doc-cell sm s-done" style={{ display: 'inline-grid', color: '#1F8A5B', border: 'none' }}>✓</span>
          Complete
        </span>
        <span>
          <span className="doc-cell sm s-missing" style={{ display: 'inline-grid', color: '#B3261E', border: 'none' }}>✕</span>
          Missing
        </span>
        <span>
          <span className="doc-cell sm s-na" style={{ display: 'inline-grid', color: '#5C7388', border: 'none' }}>—</span>
          Not required for program
        </span>
        <span className="dt-legend-note">Click any cell or row to toggle / view details</span>
      </div>
    </div>
  );
}

/* ─── Main page ─────────────────────────────────────────────────────────── */

export default function DocumentTrackerPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [cohortDates, setCohortDates]   = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [students, setStudents]         = useState([]);
  const [loading, setLoading]           = useState(false);
  const [loadingDates, setLoadingDates] = useState(true);
  const [missingOnly, setMissingOnly]   = useState(false);
  const [search, setSearch]             = useState('');
  const [chip, setChip]                 = useState('all');
  const [programFilter, setProgramFilter] = useState('');
  const [localDocs, setLocalDocs]       = useState({});
  const [drawerId, setDrawerId]         = useState(null);
  const [error, setError]               = useState('');

  // Keep a stable ref to students for use inside async callbacks
  const studentsRef = useRef(students);
  useEffect(() => { studentsRef.current = students; }, [students]);

  // Load cohort dates once
  useEffect(() => {
    api.get('/reports/cohort-dates')
      .then(res => {
        setCohortDates(res.data);
        if (res.data.length > 0) setSelectedDate(res.data[0]);
      })
      .catch(() => setError('Failed to load cohort dates'))
      .finally(() => setLoadingDates(false));
  }, []);

  // Reload students when cohort changes
  useEffect(() => {
    if (!selectedDate) return;
    setLoading(true);
    setError('');
    setLocalDocs({});
    setChip('all');
    setSearch('');
    setProgramFilter('');
    api.get(`/reports/document-tracker?cohort_start_date=${selectedDate}`)
      .then(res => setStudents(res.data))
      .catch(() => setError('Failed to load students for this cohort'))
      .finally(() => setLoading(false));
  }, [selectedDate]);

  /* Toggle a doc field — optimistic update with API revert on failure */
  function handleToggle(studentId, fieldKey) {
    const col = DOC_COLS.find(c => c.key === fieldKey);
    if (!col) return;

    const student = studentsRef.current.find(s => s.id === studentId);
    if (!student) return;
    if (!isRequired(col, student.program_code)) return;

    // Compute current value (including any local override)
    setLocalDocs(prev => {
      const override  = prev[studentId] || {};
      const rawVal    = fieldKey in override ? override[fieldKey] : student[fieldKey];
      const newVal    = !rawVal;

      // Fire the async API call as a side-effect inside the updater
      // (safe: we only read prev, not set state from an async operation)
      api.patch(`/students/${studentId}/docs`, { [fieldKey]: newVal })
        .catch(() => {
          // Revert this single field on error
          setLocalDocs(p => {
            const copy     = { ...p };
            const overrides = { ...(copy[studentId] || {}) };
            delete overrides[fieldKey];
            if (Object.keys(overrides).length === 0) delete copy[studentId];
            else copy[studentId] = overrides;
            return copy;
          });
        });

      return {
        ...prev,
        [studentId]: { ...override, [fieldKey]: newVal }
      };
    });
  }

  function exportCSV() {
    const headers = ['Last Name', 'First Name', 'Program', 'Status', ...DOC_COLS.map(c => c.short), 'Missing'];
    const rows = displayed.map(s => {
      const miss = DOC_COLS.filter(col => resolveStatus(s, col, localDocs) === 'missing').length;
      return [
        s.last_name, s.first_name,
        s.program_name || s.program_code, s.status,
        ...DOC_COLS.map(col => resolveStatus(s, col, localDocs)),
        miss,
      ];
    });
    const csv  = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `doc-tracker-${selectedDate}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  const displayed = students.filter(s => {
    if (search) {
      const q = search.toLowerCase();
      if (!`${s.first_name} ${s.last_name}`.toLowerCase().includes(q)) return false;
    }
    if (programFilter && s.program_name !== programFilter) return false;
    if (chip === 'prereq')    return PREREQ_COLS.some(col    => resolveStatus(s, col, localDocs) === 'missing');
    if (chip === 'milestone') return MILESTONE_COLS.some(col => resolveStatus(s, col, localDocs) === 'missing');
    if (chip === 'complete')  return DOC_COLS.every(col      => resolveStatus(s, col, localDocs) !== 'missing');
    if (missingOnly)          return DOC_COLS.some(col       => resolveStatus(s, col, localDocs) === 'missing');
    return true;
  });

  const isLoading = loadingDates || loading;

  return (
    <div className="page" style={{ padding: 0 }}>
      <DTHero
        cohortDates={cohortDates}
        selectedDate={selectedDate}
        onDateChange={d => setSelectedDate(d)}
        missingOnly={missingOnly}
        onToggleMissing={setMissingOnly}
        onExport={exportCSV}
      />

      <div className="h-content">
        <div className="h-content-inner">
          {students.length > 0 && !loading && (
            <DTKpis students={students} localDocs={localDocs} />
          )}

          {students.length > 0 && !loading && (
            <DTFilters
              students={students}
              localDocs={localDocs}
              search={search}
              onSearch={setSearch}
              chip={chip}
              onChip={setChip}
              program={programFilter}
              onProgram={setProgramFilter}
              isAdmin={isAdmin}
            />
          )}

          {error && (
            <div style={{ background: '#FBE7E5', border: '1px solid #F5C6C3', borderRadius: 10, padding: '12px 16px', color: '#B3261E', fontSize: 13, marginBottom: 16 }}>
              {error}
            </div>
          )}

          {isLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 220, color: 'var(--ink-3)', gap: 10 }}>
              <div style={{ width: 18, height: 18, border: '2px solid var(--line)', borderTopColor: 'var(--navy)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
              Loading…
            </div>
          ) : !selectedDate ? (
            <div style={{ textAlign: 'center', padding: 80, color: 'var(--ink-3)' }}>Select a cohort to begin</div>
          ) : displayed.length === 0 ? (
            <div className="dt-matrix-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: 'var(--ink-3)' }}>
              {students.length === 0 ? 'No students found for this cohort.' : 'No students match the current filters.'}
            </div>
          ) : (
            <DocMatrix
              students={displayed}
              localDocs={localDocs}
              onToggle={handleToggle}
              onRowClick={id => setDrawerId(id)}
            />
          )}
        </div>
      </div>

      <StudentDrawer
        studentId={drawerId}
        students={students}
        localDocs={localDocs}
        onToggle={handleToggle}
        onClose={() => setDrawerId(null)}
      />
    </div>
  );
}
